/**
 * Follow-up parameters handling for different AI providers
 */
import { cleanFollowUpParams } from "./utils";

/**
 * Generate Mistral-compatible alphanumeric ID
 * @returns {string} 9-character alphanumeric ID
 */
function generateMistralId() {
  const alphanumeric =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let id = "";
  for (let i = 0; i < 9; i++) {
    id += alphanumeric.charAt(Math.floor(Math.random() * alphanumeric.length));
  }
  return id;
}

/**
 * Handle Anthropic-specific follow-up parameters
 * @param {Object} params - Parameters object
 * @returns {Object} Formatted parameters
 */
function handleAnthropicFollowUp({
  modelProvider,
  modelName,
  mutableFollowUpMessages,
  toolResults,
  enhancedInstructions,
  contextData,
  providerFactory,
}) {
  // Backend provider system doesn't need provider factory

  // For backend providers, we use the messages as-is since they're already properly formatted
  const followUpParams = {
    model: modelName,
    messages: mutableFollowUpMessages,
    stream: true,
    temperature: contextData.temperature || 0.2,
    max_tokens: contextData.max_tokens || 1500,
    top_p: contextData.top_p || 1,
    ...(contextData.anthropic_version
      ? { anthropic_version: contextData.anthropic_version }
      : {}),
    ...(contextData.anthropic_beta
      ? { anthropic_beta: contextData.anthropic_beta }
      : {}),
  };

  // CRITICAL FIX: Add system instructions for Anthropic providers
  if (enhancedInstructions && enhancedInstructions.trim()) {
    // Check if there's already a system message
    const hasSystemMessage = followUpParams.messages.some(
      (m) => m.role === "system"
    );

    if (!hasSystemMessage) {
      // Add system message at the beginning
      followUpParams.messages.unshift({
        role: "system",
        content: enhancedInstructions,
      });
    } else {
      // Update existing system message
      const systemMessageIndex = followUpParams.messages.findIndex(
        (m) => m.role === "system"
      );
      if (systemMessageIndex !== -1) {
        followUpParams.messages[systemMessageIndex].content =
          enhancedInstructions;
      }
    }
  }

  return followUpParams;
}

/**
 * Handle Mistral-specific message formatting
 * @param {Object} followUpParams - Follow-up parameters
 * @returns {Object} Formatted parameters
 */
function handleMistralFormatting(followUpParams) {
  followUpParams.messages = followUpParams.messages.map((message) => {
    if (message.role === "user") {
      return {
        role: "user",
        content: message.content || "",
      };
    } else if (message.role === "assistant") {
      const cleanedMessage = {
        role: "assistant",
      };

      if (message.content) {
        cleanedMessage.content = message.content;
      }

      if (message.tool_calls && message.tool_calls.length > 0) {
        const toolCallIdMap = new Map();

        cleanedMessage.tool_calls = message.tool_calls.map((toolCall) => {
          let mistralId = toolCall.id;

          if (!/^[a-zA-Z0-9]{9}$/.test(mistralId)) {
            const newId = generateMistralId();
            toolCallIdMap.set(toolCall.id, newId);
            mistralId = newId;
          }

          return {
            id: mistralId,
            type: toolCall.type || "function",
            function: {
              name: toolCall.function?.name || "",
              arguments: toolCall.function?.arguments || "{}",
            },
          };
        });

        if (toolCallIdMap.size > 0) {
          window.__mistralToolCallIdMap = toolCallIdMap;
        }
      }

      if (
        !cleanedMessage.content &&
        (!cleanedMessage.tool_calls || !cleanedMessage.tool_calls.length)
      ) {
        cleanedMessage.content = "";
      }

      return cleanedMessage;
    } else if (message.role === "tool") {
      let mistralToolCallId = message.tool_call_id;

      if (
        window.__mistralToolCallIdMap &&
        window.__mistralToolCallIdMap.has(mistralToolCallId)
      ) {
        const mappedId = window.__mistralToolCallIdMap.get(mistralToolCallId);
        mistralToolCallId = mappedId;
      } else if (!/^[a-zA-Z0-9]{9}$/.test(mistralToolCallId)) {
        mistralToolCallId = generateMistralId();

        if (window.__mistralToolCallIdMap) {
          window.__mistralToolCallIdMap.set(
            message.tool_call_id,
            mistralToolCallId
          );
        } else {
          window.__mistralToolCallIdMap = new Map([
            [message.tool_call_id, mistralToolCallId],
          ]);
        }
      }

      return {
        role: "tool",
        tool_call_id: mistralToolCallId,
        name: message.name,
        content: message.content || "",
      };
    } else if (message.role === "system") {
      return {
        role: "system",
        content: message.content || "",
      };
    }

    return message;
  });

  return followUpParams;
}

/**
 * Handle Google/Gemini-specific message formatting
 * @param {Object} followUpParams - Follow-up parameters
 * @returns {Object} Formatted parameters
 */
function handleGeminiFormatting(followUpParams) {
  followUpParams.messages = followUpParams.messages.map((message) => {
    if (!message.content) {
      if (
        message.role === "assistant" &&
        message.tool_calls &&
        message.tool_calls.length > 0
      ) {
        const toolNames = message.tool_calls
          .map((tc) => tc.function?.name || "unknown")
          .join(", ");
        return {
          ...message,
          content: `The assistant called the following tool(s): ${toolNames}`,
        };
      } else if (message.role === "tool" && message.tool_call_id) {
        return {
          ...message,
          content: "The tool provided a response.",
        };
      } else if (message.role === "user") {
        return {
          ...message,
          content: "Please process the tool results and provide a response.",
        };
      } else {
        return {
          ...message,
          content: "Please continue the conversation.",
        };
      }
    }
    return message;
  });

  delete followUpParams.tools;
  delete followUpParams.tool_choice;

  return followUpParams;
}

/**
 * Handle OpenAI-specific message validation and repair
 * @param {Object} followUpParams - Follow-up parameters
 * @param {Object} assistantMessage - Original assistant message
 * @param {Array} toolResults - Tool execution results
 * @param {Array} userMessages - User messages
 * @param {Function} generateUUID - UUID generator function
 * @returns {Object} Formatted parameters
 */
function handleOpenAIFormatting(
  followUpParams,
  assistantMessage,
  toolResults,
  userMessages,
  generateUUID
) {
  const processedMessages = followUpParams.messages;

  const assistantMsgs = processedMessages.filter((m) => m.role === "assistant");
  const toolMsgs = processedMessages.filter((m) => m.role === "tool");

  if (assistantMsgs.length > 0 && toolMsgs.length > 0) {
    let needsRepair = false;
    const assistantWithToolCalls = assistantMsgs.find(
      (m) => m.tool_calls && m.tool_calls.length > 0
    );

    if (!assistantWithToolCalls) {
      if (assistantMsgs.length > 0 && assistantMessage.tool_calls) {
        const idx = processedMessages.findIndex((m) => m === assistantMsgs[0]);
        if (idx >= 0) {
          processedMessages[idx] = JSON.parse(JSON.stringify(assistantMessage));
          needsRepair = true;
        }
      }
    }

    const revisedAssistantWithToolCalls = processedMessages.find(
      (m) => m.role === "tool" && m.tool_calls && m.tool_calls.length > 0
    );

    if (revisedAssistantWithToolCalls) {
      // Validate tool call IDs match between assistant and tool messages
      for (const toolMsg of toolMsgs) {
        const toolCallId = toolMsg.tool_call_id;
        const assistantHasToolCallId =
          revisedAssistantWithToolCalls.tool_calls.some(
            (tc) => tc.id === toolCallId
          );

        if (!assistantHasToolCallId) {
          const toolFunctionName = toolResults.find(
            (result) => result.toolCall.id === toolCallId
          )?.toolCall?.function?.name;

          if (toolFunctionName) {
            const matchingToolCall =
              revisedAssistantWithToolCalls.tool_calls.find(
                (tc) => tc.function?.name === toolFunctionName
              );

            if (matchingToolCall) {
              toolMsg.tool_call_id = matchingToolCall.id;
              needsRepair = true;
            } else {
              revisedAssistantWithToolCalls.tool_calls.push({
                id: toolCallId,
                type: "function",
                function: {
                  name: toolFunctionName || "unknown_function",
                  arguments: "{}",
                },
              });
              needsRepair = true;
            }
          } else {
            revisedAssistantWithToolCalls.tool_calls.push({
              id: toolCallId,
              type: "function",
              function: {
                name: "unknown_function",
                arguments: "{}",
              },
            });
            needsRepair = true;
          }
        }
      }

      // Handle duplicate tool call IDs
      const toolMsgIds = new Map();
      for (const toolMsg of toolMsgs) {
        if (toolMsgIds.has(toolMsg.tool_call_id)) {
          console.warn(`Found duplicate tool_call_id: ${toolMsg.tool_call_id}`);

          const unusedToolCall = revisedAssistantWithToolCalls.tool_calls.find(
            (tc) =>
              !toolMsgIds.has(tc.id) &&
              !toolMsgs.some(
                (tm) => tm !== toolMsg && tm.tool_call_id === tc.id
              )
          );

          if (unusedToolCall) {
            toolMsg.tool_call_id = unusedToolCall.id;
            toolMsgIds.set(unusedToolCall.id, true);
            needsRepair = true;
          } else {
            const newId = `call_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
            toolMsg.tool_call_id = newId;

            revisedAssistantWithToolCalls.tool_calls.push({
              id: newId,
              type: "function",
              function: {
                name: toolMsg.name || "unknown_function",
                arguments: "{}",
              },
            });

            toolMsgIds.set(newId, true);
            needsRepair = true;
          }
        } else {
          toolMsgIds.set(toolMsg.tool_call_id, true);
        }
      }

      // Ensure proper message ordering (assistant before tool messages)
      const assistantIdx = processedMessages.findIndex(
        (m) => m === revisedAssistantWithToolCalls
      );
      let reordered = false;

      for (let i = 0; i < processedMessages.length; i++) {
        if (processedMessages[i].role === "tool" && i < assistantIdx) {
          reordered = true;
          break;
        }
      }

      if (reordered) {
        const newMessages = [];

        processedMessages.forEach((m) => {
          if (m.role !== "assistant" && m.role !== "tool") {
            newMessages.push(m);
          }
        });

        newMessages.push(revisedAssistantWithToolCalls);

        toolMsgs.forEach((toolMsg) => {
          newMessages.push(toolMsg);
        });

        followUpParams.messages = newMessages;
        needsRepair = true;
      }
    } else {
      const userId =
        userMessages[userMessages.length - 1]?.id || generateUUID();
      followUpParams.messages = [
        {
          role: "user",
          content:
            userMessages[userMessages.length - 1]?.content ||
            "Please respond to the tool results",
          id: userId,
        },
        JSON.parse(JSON.stringify(assistantMessage)), // Use our original assistant message
        ...toolMsgs, // Keep all tool messages
      ];
    }
  }

  return followUpParams;
}

/**
 * Handle provider-specific tool disabling for follow-up requests
 * @param {Object} followUpParams - Follow-up parameters
 * @param {string} actualProviderName - Provider name
 * @returns {Object} Modified parameters
 */
function handleToolDisabling(followUpParams, actualProviderName) {
  if (actualProviderName === "deepseek" || actualProviderName === "anthropic") {
    delete followUpParams.tools;
    delete followUpParams.tool_choice;

    const userMessages = followUpParams.messages.filter(
      (m) => m.role === "user"
    );
    const lastUserMessageIndex = followUpParams.messages.findIndex(
      (m, i) =>
        m.role === "user" &&
        i ===
          followUpParams.messages.lastIndexOf(
            userMessages[userMessages.length - 1]
          )
    );

    if (lastUserMessageIndex !== -1) {
      const lastUserMessage = followUpParams.messages[lastUserMessageIndex];
      followUpParams.messages[lastUserMessageIndex] = {
        ...lastUserMessage,
        content:
          (lastUserMessage.content || "") +
          "\n\nPlease respond directly to my question based on the tool results above. Do not make any additional tool calls.",
      };
    }
  }
  return followUpParams;
}

/**
 * Create and format follow-up parameters for different AI providers
 * @param {Object} params - Configuration object
 * @returns {Object} Formatted follow-up parameters
 */
export function createFollowUpParams({
  modelProvider,
  modelName,
  mutableFollowUpMessages,
  toolResults,
  enhancedInstructions,
  contextData,
  providerFactory,
  actualProviderName,
  assistantMessage,
  userMessages,
  generateUUID,
}) {
  let followUpParams;

  // Handle provider-specific parameter creation
  if (modelProvider === "anthropic") {
    followUpParams = handleAnthropicFollowUp({
      modelProvider,
      modelName,
      mutableFollowUpMessages,
      toolResults,
      enhancedInstructions,
      contextData,
      providerFactory,
    });
  } else {
    // Default parameters for other providers
    followUpParams = {
      model: modelName,
      messages: mutableFollowUpMessages,
      stream: true,
      temperature: contextData.temperature || 0.2,
      max_tokens: contextData.max_tokens || 16000,
      top_p: contextData.top_p || 1,
      ...(contextData.anthropic_version
        ? { anthropic_version: contextData.anthropic_version }
        : {}),
      ...(contextData.anthropic_beta
        ? { anthropic_beta: contextData.anthropic_beta }
        : {}),
    };

    // CRITICAL FIX: Add system instructions for non-Anthropic providers
    if (enhancedInstructions && enhancedInstructions.trim()) {
      // Check if there's already a system message
      const hasSystemMessage = followUpParams.messages.some(
        (m) => m.role === "system"
      );

      if (!hasSystemMessage) {
        // Add system message at the beginning
        followUpParams.messages.unshift({
          role: "system",
          content: enhancedInstructions,
        });
      } else {
        // Update existing system message
        const systemMessageIndex = followUpParams.messages.findIndex(
          (m) => m.role === "system"
        );
        if (systemMessageIndex !== -1) {
          followUpParams.messages[systemMessageIndex].content =
            enhancedInstructions;
        }
      }
    }
  }

  // Clean parameters using existing utility
  followUpParams = cleanFollowUpParams(
    followUpParams,
    modelName,
    actualProviderName
  );

  followUpParams = handleToolDisabling(followUpParams, actualProviderName);

  // Apply provider-specific message formatting
  if (actualProviderName === "mistral") {
    followUpParams = handleMistralFormatting(followUpParams);
  } else if (
    actualProviderName === "google" ||
    actualProviderName === "gemini"
  ) {
    followUpParams = handleGeminiFormatting(followUpParams);
  } else if (actualProviderName === "openai") {
    followUpParams = handleOpenAIFormatting(
      followUpParams,
      assistantMessage,
      toolResults,
      userMessages,
      generateUUID
    );
  }

  // CRITICAL FIX: Final safeguard to ensure system message and instructions are always included
  if (enhancedInstructions && enhancedInstructions.trim()) {
    // Always set the instructions parameter as a backup
    followUpParams.instructions = enhancedInstructions;

    // Double-check that system message exists after provider formatting
    const hasSystemMessage = followUpParams.messages.some(
      (m) => m.role === "system"
    );

    if (!hasSystemMessage) {
      // Add system message at the beginning
      followUpParams.messages.unshift({
        role: "system",
        content: enhancedInstructions,
      });
    } else {
      // Verify system message content is correct
      const systemMessage = followUpParams.messages.find(
        (m) => m.role === "system"
      );
      if (
        !systemMessage.content ||
        systemMessage.content.trim() !== enhancedInstructions.trim()
      ) {
        systemMessage.content = enhancedInstructions;
      }
    }
  }

  return followUpParams;
}
